import { Record } from 'immutable';
import type { RecordOf } from 'immutable';

import type { ValueOf } from 'type-fest';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import type { transactionsTypes } from '../transactions';
import type { RecurrenceType } from './customFrequencyTypes';
import { FrequencyEnum } from './frequency';

export const SCHEDULED_TRANSACTIONS_REDUCER_KEY = 'scheduledTransactionsStore';

export const mkRecurrence = Record<RecurrenceType>({
  frequency: FrequencyEnum.MONTHLY,
  interval: 1,
  count: null,
  byMonthDay: null,
  byDay: null,
  bySetPos: null,
  byMonth: null,
  byYearDay: null,
  byDates: null,
  endOn: null,
  alias: null,
});

export const STTypeEnum = Object.freeze({
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  TRANSFER: 'TRANSFER',
  BILL: 'BILL',
  SUBSCRIPTION: 'SUBSCRIPTION',
  UNKNOWN: 'UNKNOWN',
});

export const STExcludeEnum = Object.freeze({
  SRC: 'SRC',
  DEST: 'DEST',
  BOTH: 'BOTH',
  NONE: 'NONE',
});
export type STType = ValueOf<typeof STTypeEnum>;
export type STExclude = ValueOf<typeof STExcludeEnum>;

export type AutoAdjustAmount =
  | 'AVERAGE_PAID'
  | 'EBILL_BALANCE_DUE'
  | 'EBILL_MINIMUM_DUE'
  | 'EBILL_LAST_STATEMENT_BALANCE'
  | 'TIME_OF_YEAR'
  | 'NONE'
  | null;

export const AutoAdjustAmountEnum = Object.freeze({
  statement: 'EBILL_LAST_STATEMENT_BALANCE',
  minimum: 'EBILL_MINIMUM_DUE',
  total: 'EBILL_BALANCE_DUE',
  none: 'NONE',
  average: 'AVERAGE_PAID',
  time: 'TIME_OF_YEAR',
});

export type BillerType = 'FISERV' | 'BVA'; // only fiServ expected for Simplifi billerAccounts

export const BillerStatusEnum = Object.freeze({
  PENDING: 'PENDING',
  CONNECTED: 'CONNECTED',
  FAILED: 'FAILED',
  DISCONNECTED: 'DISCONNECTED',
});
export type BillerStatus = ValueOf<typeof BillerStatusEnum>;

/**
 * This is important for scheduled transfers
 * NONE -> Not excluded at all
 * SRC -> Only the source side is excluded
 * DEST -> Only destination is excluded
 * BOTH -> Both sides are excluded
 * For non-transfer series SRC, DEST, and BOTH are all the same.
 */
export type ExclusionVariant = 'NONE' | 'SRC' | 'DEST' | 'BOTH';

export type ScheduledTransactionType = {
  id?: string;
  clientId?: string;
  isDeleted?: boolean;
  createdAt?: string;
  modifiedAt?: string | null;
  userModifiedAt?: string | null;
  dueOn?: string;
  type?: string;
  recurrence?: RecurrenceType | RecordOf<RecurrenceType>;
  transaction?: transactionsTypes.TransactionProps | transactionsTypes.TransactionRecord | null;
  overrideNextDueOn?: string | null;
  overrideNextAmount?: number | null;
  reminderDays?: number | null;
  autoAcceptDays?: number | null;
  autoSendDays?: number | null;
  autoAdjustAmount?: AutoAdjustAmount;
  autoAdjustDueOn?: boolean | null;
  averagePaidCount?: number | null;
  paymentProvider?: string | null;
  paymentModelId?: string | null;
  // deprecated BillGo fields
  providerBillerId?: string | null; // deprecated - was used in BillGO to lookup the biller
  billPresentmentAccountId?: string | null; // deprecated - was used by billGO to lookup the billerAccount
  billPresentmentBillerId?: string | null; // deprecated - was used bu BillGO to manage billerlogin ID
  billPresentmentProvider?: string | null; // deprecated - BillGO use
  billPresentmentModelId?: string | null; // deprecated - BillGO use
  // fiServ fields
  presentmentAccountId?: string | null; // billerAccount id
  billSnapshot?: any; // raw bill payload
  billerType?: BillerType | null;
  billerEnrollmentStatus?: BillerStatus | null;

  relatedWebsite?: string | null;
  printCheck?: boolean | null;
  isCompleted?: boolean | null;
  isUserVerified?: boolean | null;
  excludeF2S?: ExclusionVariant | null;
  excludeReports?: ExclusionVariant | null;
  resetMatchAmountVariance?: boolean | null;
  matchAnyAmount?: boolean | null;
  matchAmountVariance?: number | null;
};

export type ScheduledTransaction = RecordOf<ScheduledTransactionType>;
export const mkScheduledTransaction = Record<ScheduledTransactionType>({
  id: undefined,
  clientId: undefined,
  isDeleted: undefined,
  createdAt: undefined,
  modifiedAt: undefined,
  userModifiedAt: undefined,
  dueOn: undefined,
  type: undefined,
  recurrence: undefined,
  transaction: undefined,
  overrideNextDueOn: undefined,
  overrideNextAmount: undefined,
  reminderDays: undefined,
  autoAcceptDays: undefined,
  autoSendDays: undefined,
  autoAdjustAmount: undefined,
  autoAdjustDueOn: undefined,
  averagePaidCount: undefined,
  paymentProvider: undefined,
  paymentModelId: undefined,
  // deprecated BillGo fields
  providerBillerId: undefined,
  billPresentmentAccountId: undefined,
  billPresentmentBillerId: undefined,
  billPresentmentProvider: undefined,
  billPresentmentModelId: undefined,
  // fiServ fields
  presentmentAccountId: undefined,
  billSnapshot: undefined,
  billerType: undefined,
  billerEnrollmentStatus: undefined,

  relatedWebsite: undefined,
  printCheck: undefined,
  isCompleted: false, // that's important for undo
  isUserVerified: undefined,
  excludeF2S: undefined,
  excludeReports: undefined,
  resetMatchAmountVariance: undefined,
  matchAnyAmount: undefined,
  matchAmountVariance: undefined,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [SCHEDULED_TRANSACTIONS_REDUCER_KEY]: QcsSyncResourceStore<ScheduledTransactionType>;
  }
}
